/**
 * 预算护栏（前端预检）—— 与后端 `DEFAULT_BUDGET_GUARD_POLICY` **同一套规则**。
 *
 * 真源：`apps/api/src/actions.ts` 的 `DEFAULT_BUDGET_GUARD_POLICY` 与
 * `packages/meta/src/mutations.ts` 的 `checkBudgetChange`（2026-10-04 第十三批 · 用户第 2 条）：
 *   - **调低（新值 < 旧值）：不限幅度**（旧 ±20% 规则已作废）；
 *   - **调高：单次最多 3 倍**（`新值 ≤ 旧值 × 3`，旧值 > 0 时才判）；
 *   - 新额度在 1.00 – 100000.00 之间（账户币种最小单位整数：100 – 10_000_000 分）；
 *   - 必须是 1.00 的整数倍（`multipleOfMinorUnits = 100` 分）。
 *
 * 为什么前端要再算一遍：预检能在**发请求之前**就把「不可能通过的目标」标出来，
 * 省掉一次 dry-run 往返；但它**不是**安全边界 —— 后端仍会独立判定（见 actions.ts
 * 的四道闸门）。前端预检通过 ≠ 后端一定放行。
 *
 * 金额一律整数分；调高用整数乘法比较（`after > before × multiplier`），
 * 因此「恰好等于 3 倍」必然不报 issue，与后端逐位一致。
 */

export interface BudgetGuardPolicy {
  /**
   * 单次**调高**的最大倍数（正整数，例如 3 = 最多调到旧值的 3 倍）。
   * 旧值为 `null` / `0` 时不判倍数（没有基准），只受 min/max/倍数格式约束。
   */
  readonly maxIncreaseMultiplier: number;
  /** 最小额度（最小单位整数）。 */
  readonly minMinorUnits: number;
  /** 最大额度。 */
  readonly maxMinorUnits: number;
  /** 要求新额度必须是这个数的整数倍。0 = 不要求。 */
  readonly multipleOfMinorUnits: number;
}

/** 与后端 `DEFAULT_BUDGET_GUARD_POLICY` 数值完全一致（调高 3 倍 / 调低不限）。 */
export const DEFAULT_BUDGET_GUARD_POLICY: BudgetGuardPolicy = {
  maxIncreaseMultiplier: 3,
  minMinorUnits: 100,
  maxMinorUnits: 10_000_000,
  multipleOfMinorUnits: 100,
};

export type BudgetGuardIssueCode =
  | "NOT_POSITIVE_INTEGER"
  | "BELOW_MIN"
  | "ABOVE_MAX"
  | "INCREASE_TOO_LARGE"
  | "NOT_A_MULTIPLE"
  | "UNCHANGED";

export interface BudgetGuardIssue {
  readonly code: BudgetGuardIssueCode;
  readonly message: string;
}

function isValidBefore(value: number | null): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/**
 * 预算护栏：返回**全部**命中的 issue（顺序固定，便于 UI 逐条显示与测试断言）。
 * 与后端 `checkBudgetChange` 的判定顺序、code、边界完全一致。
 *
 * 调低不产生任何幅度类 issue；调高超过 `maxIncreaseMultiplier` 倍才报
 * `INCREASE_TOO_LARGE`（文案说人话：「单次最多调高到 3 倍」）。
 */
export function checkBudgetChange(input: {
  readonly beforeMinorUnits: number | null;
  readonly afterMinorUnits: number;
  readonly policy?: BudgetGuardPolicy;
}): readonly BudgetGuardIssue[] {
  const policy = input.policy ?? DEFAULT_BUDGET_GUARD_POLICY;
  const issues: BudgetGuardIssue[] = [];
  const before = isValidBefore(input.beforeMinorUnits) ? input.beforeMinorUnits : null;
  const after = input.afterMinorUnits;

  if (typeof after !== "number" || !Number.isSafeInteger(after) || after <= 0) {
    return [
      {
        code: "NOT_POSITIVE_INTEGER",
        message: `新额度必须是正整数的最小单位金额（不接受小数/0/负数）：${JSON.stringify(after)}`,
      },
    ];
  }

  if (before !== null && after === before) {
    issues.push({
      code: "UNCHANGED",
      message: `新额度与当前额度相同（${after}），提交后不会产生任何变更`,
    });
  }

  if (after < policy.minMinorUnits) {
    issues.push({
      code: "BELOW_MIN",
      message: `新额度 ${after} 低于下限 ${policy.minMinorUnits}（最小单位）`,
    });
  }
  if (after > policy.maxMinorUnits) {
    issues.push({
      code: "ABOVE_MAX",
      message: `新额度 ${after} 高于上限 ${policy.maxMinorUnits}（最小单位）`,
    });
  }

  // 只判调高；调低不限幅度。旧值为 null/0 时不判倍数（没有基准）。
  if (before !== null && before > 0 && after > before) {
    const limit = before * policy.maxIncreaseMultiplier;
    if (Number.isSafeInteger(limit) && after > limit) {
      issues.push({
        code: "INCREASE_TOO_LARGE",
        message:
          `单次最多调高到 ${policy.maxIncreaseMultiplier} 倍：当前 ${before}，上限 ${limit}，` +
          `实际 ${after}；改小不限`,
      });
    }
  }

  if (policy.multipleOfMinorUnits > 0 && after % policy.multipleOfMinorUnits !== 0) {
    issues.push({
      code: "NOT_A_MULTIPLE",
      message: `新额度 ${after} 不是 ${policy.multipleOfMinorUnits} 的整数倍（最小单位的最小步长）`,
    });
  }

  return issues;
}

/** 是否存在**硬错误**（`UNCHANGED` 只是提示，不阻止提交？—— 阻止，避免「成功的空操作」）。 */
export function hasBlockingBudgetIssue(issues: readonly BudgetGuardIssue[]): boolean {
  return issues.length > 0;
}

/**
 * 把用户输入的「主单位金额」解析成整数分（1 主单位 = 100 分）。
 *
 * 规则：最多两位小数（货币最小单位的常见形状）；拒绝空串、负数、指数、超两位小数、
 * 超出安全整数。**不做四舍五入**——悄悄改掉用户输入的金额比报错危险。
 */
export function parseAmountToMinorUnits(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const parts = trimmed.split(".");
  const whole = parts[0] ?? "0";
  const fraction = (parts[1] ?? "").padEnd(2, "0");
  const minorUnits = Number(whole) * 100 + Number(fraction.slice(0, 2));
  if (!Number.isSafeInteger(minorUnits)) return null;
  return minorUnits;
}
